// Панель с экрана «Домой» на iPhone: известная ошибка iOS. После первого
// появления клавиатуры страница укорачивается на высоту строки состояния
// (innerHeight, visualViewport и 100dvh — все разом) и не восстанавливается до
// перезапуска приложения. Нижнее меню повисает над низом экрана, а окна «во
// весь экран» сдвигаются вверх, под часы.
//
// Лечится тем, что после закрытия клавиатуры элемент во всю высоту экрана на
// миг скрывается и показывается снова: iOS при этом измеряет экран заново.
// Скрытие и показ — в одном шаге, без отрисовки между ними, поэтому мигания
// нет; прокрутка, которую сброс показа обнуляет, возвращается на место.
// Приём описан здесь: dev.to/cederhook/fixing-the-ios-standalone-pwa-keyboard-
// bug-that-shrinks-your-viewport-for-good-63d
//
// Отдельно — на любом iPhone, и во вкладке Safari тоже: слежка за спрятанной
// клавиатурой (installKeyboardDismissTracking ниже). На других устройствах
// ничего не делает.

import { isIosDevice, isStandaloneApp } from "./webPush";

// Укорочение меньше этого — не ошибка, а округление.
const SHRINK_TOLERANCE_PX = 4;
const HEAL_DELAY_MS = 150;

const isEditable = (node) => Boolean(node) && (
  node.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(node.tagName || "")
);

const isPortrait = () => window.innerWidth <= window.innerHeight;

// Высота, которой страница должна быть: наибольшая, что уже встречалась в
// этой ориентации. В портретной — не меньше высоты экрана: укороченной
// страница могла быть с самого запуска.
const expectedHeight = (seenMax) => {
  const screenHeight = isPortrait()
    ? Math.max(Number(window.screen?.height) || 0, Number(window.screen?.width) || 0)
    : 0;
  return Math.max(seenMax, screenHeight);
};

const collectScrolled = (root) => {
  const scrolled = root.scrollTop > 0 ? [[root, root.scrollTop]] : [];
  root.querySelectorAll("*").forEach((node) => {
    if (node.scrollTop > 0) scrolled.push([node, node.scrollTop]);
  });
  return scrolled;
};

// Клавиатура открыта: видимая часть страницы ниже её высоты на столько.
const KEYBOARD_MIN_PX = 120;

// Курсор в поле, а клавиатуры нет (на iPhone так бывает): поле ответа
// рассчитывает, что под ним клавиатура, и прижимается к низу экрана — под
// скруглённые углы. Класс kb-dismissed на <html> (см. index.css) возвращает
// ему отступ, пока клавиатура не откроется или поле не потеряет фокус.
//
// Бывает это двумя путями:
// — клавиатуру спрятали, а курсор остался;
// — курсор поставлен в поле не касанием, а из кода («Вставить» подсказку
//   нейросети, быстрый ответ): iOS даёт полю фокус, но клавиатуру без
//   касания не открывает. Строка опускалась и оставалась внизу и после
//   отправки, пока не перезайдёшь в диалог.
// Касание самого поля — не наш случай: отступ убирается сразу, ещё до
// появления клавиатуры, чтобы поле не сдвигалось после того, как iOS
// поставила курсор.
const FOCUS_TOUCH_WINDOW_MS = 1000;
const installKeyboardDismissTracking = () => {
  const viewport = window.visualViewport;
  if (!viewport) return () => {};
  const root = document.documentElement;
  const isKeyboardOpen = () => window.innerHeight - viewport.height > KEYBOARD_MIN_PX;
  let keyboardOpen = isKeyboardOpen();
  let lastEditableTouchAt = 0;
  const onViewportResize = () => {
    if (isKeyboardOpen()) {
      keyboardOpen = true;
      root.classList.remove("kb-dismissed");
      return;
    }
    if (keyboardOpen && isEditable(document.activeElement)) root.classList.add("kb-dismissed");
    keyboardOpen = false;
  };
  const onTouchStart = (event) => {
    if (!isEditable(event.target)) return;
    lastEditableTouchAt = Date.now();
    // Касание поля, где курсор уже стоит без клавиатуры: она сейчас откроется
    // — отступ убирается сразу, как и при обычном касании.
    if (event.target === document.activeElement) root.classList.remove("kb-dismissed");
  };
  const onFocusIn = (event) => {
    if (!isEditable(event.target)) return;
    const fromTouch = Date.now() - lastEditableTouchAt < FOCUS_TOUCH_WINDOW_MS;
    if (!fromTouch && !isKeyboardOpen()) root.classList.add("kb-dismissed");
  };
  const onFocusOut = () => root.classList.remove("kb-dismissed");
  viewport.addEventListener("resize", onViewportResize);
  document.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
  document.addEventListener("focusin", onFocusIn);
  document.addEventListener("focusout", onFocusOut);
  return () => {
    viewport.removeEventListener("resize", onViewportResize);
    document.removeEventListener("touchstart", onTouchStart, { capture: true });
    document.removeEventListener("focusin", onFocusIn);
    document.removeEventListener("focusout", onFocusOut);
    root.classList.remove("kb-dismissed");
  };
};

export const installStandaloneViewportHeal = () => {
  if (typeof window === "undefined" || !isIosDevice()) return () => {};
  const stopKeyboardTracking = installKeyboardDismissTracking();
  if (!isStandaloneApp()) return stopKeyboardTracking;
  let seenMax = window.innerHeight;
  let portrait = isPortrait();
  let timer = 0;
  // Высота, которую пересчёт не исправил (окно на iPad меньше экрана, iOS
  // без этой ошибки): с ней больше не пытаемся, пока высота не изменится.
  let unhealedHeight = 0;

  const heal = () => {
    timer = 0;
    // Клавиатура ещё открыта — ждём, пока закроется.
    if (isEditable(document.activeElement)) return;
    const before = window.innerHeight;
    if (expectedHeight(seenMax) - before <= SHRINK_TOLERANCE_PX) return;
    if (before === unhealedHeight) return;
    const root = document.querySelector(".app-root");
    if (!root) return;
    const scrolled = collectScrolled(root);
    root.style.display = "none";
    void root.offsetHeight;
    root.style.display = "";
    scrolled.forEach(([node, top]) => { node.scrollTop = top; });
    window.requestAnimationFrame(() => {
      unhealedHeight = window.innerHeight <= before ? before : 0;
    });
  };

  const scheduleHeal = () => {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(heal, HEAL_DELAY_MS);
  };

  const onResize = () => {
    const nowPortrait = isPortrait();
    if (nowPortrait !== portrait) {
      // Повернули — в новой ориентации своя высота.
      portrait = nowPortrait;
      seenMax = window.innerHeight;
    } else {
      seenMax = Math.max(seenMax, window.innerHeight);
    }
    scheduleHeal();
  };

  const onVisibility = () => {
    if (!document.hidden) scheduleHeal();
  };

  document.addEventListener("focusout", scheduleHeal);
  window.addEventListener("resize", onResize);
  window.visualViewport?.addEventListener("resize", scheduleHeal);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pageshow", scheduleHeal);
  // Страница могла запуститься уже укороченной.
  scheduleHeal();

  return () => {
    stopKeyboardTracking();
    if (timer) window.clearTimeout(timer);
    document.removeEventListener("focusout", scheduleHeal);
    window.removeEventListener("resize", onResize);
    window.visualViewport?.removeEventListener("resize", scheduleHeal);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pageshow", scheduleHeal);
  };
};
